import { useState } from 'react';
import { errorText } from '@infrastructure/api/errors';

import type { NewReseller } from '@domain/reseller/Reseller';
import type { HttpResellerRepository } from '@infrastructure/api/HttpResellerRepository';
import { Modal } from '@app/components/Modal';

// Where resellers bind (talkysms-smpp-reseller listens on 0.0.0.0:2775;
// server.talky.ca resolves to this VM). There is no TLS listener, so the
// form doesn't offer "require TLS": such a reseller could never bind.
const SMPP_HOST = 'server.talky.ca';
const SMPP_PORT = 2775;

const TIERS: { value: NewReseller['tier_level']; label: string }[] = [
  { value: 'bronze', label: 'Bronze — $0.012/segment, 10% commission' },
  { value: 'silver', label: 'Silver — $0.010/segment, 15% commission' },
  { value: 'gold', label: 'Gold — $0.008/segment, 20% commission' },
  { value: 'partner', label: 'Partner — $0.006/segment, 25% commission' },
];

interface Created { id: string; name: string; system_id: string; password: string }

export function CreateResellerModal({
  open, repo, onClose, onOpenReseller,
}: {
  open: boolean;
  repo: HttpResellerRepository;
  onClose: () => void;
  onOpenReseller: (id: string) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [tier, setTier] = useState<NewReseller['tier_level']>('bronze');
  const [billing, setBilling] = useState<NewReseller['billing_model']>('prepaid');
  const [creditLimit, setCreditLimit] = useState('500');
  const [maxConnections, setMaxConnections] = useState('5');
  const [ratePerSecond, setRatePerSecond] = useState('100');
  const [dailyQuota, setDailyQuota] = useState('50000');
  const [ips, setIps] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<Created | null>(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setName(''); setEmail(''); setPhone(''); setTier('bronze'); setBilling('prepaid');
    setCreditLimit('500'); setMaxConnections('5'); setRatePerSecond('100'); setDailyQuota('50000');
    setIps(''); setError(null); setCreated(null); setCopied(false);
  };
  const close = () => { reset(); onClose(); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null); setSubmitting(true);
    try {
      const r = await repo.create({
        name: name.trim(),
        contact_email: email.trim() || undefined,
        contact_phone: phone.trim() || undefined,
        tier_level: tier,
        billing_model: billing,
        credit_limit: billing === 'postpaid' ? creditLimit : undefined,
        max_connections: Number(maxConnections),
        rate_limit_per_second: Number(ratePerSecond),
        daily_quota: Number(dailyQuota),
        allowed_ips: ips.split(',').map((s) => s.trim()).filter(Boolean),
      });
      setCreated({ id: r.id, name: r.name, system_id: r.system_id, password: r.generated_password });
    } catch (err) {
      setError(errorText(err));
    } finally { setSubmitting(false); }
  };

  if (created) {
    const text = `Host: ${SMPP_HOST}\nPort: ${SMPP_PORT}\nSystem ID: ${created.system_id}\nPassword: ${created.password}`;
    return (
      <Modal open={open} title={`Reseller created — ${created.name}`} onClose={close}>
        <div className="space-y-3 text-sm">
          <p className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Copy these now: the password is not shown again (Rotate password makes a new one).
            The reseller is <strong>pending</strong>; activate it on its page before it can bind.
          </p>
          <dl className="grid grid-cols-3 gap-y-1 font-mono text-xs">
            <dt className="text-slate-500">Host</dt><dd className="col-span-2">{SMPP_HOST}</dd>
            <dt className="text-slate-500">Port</dt><dd className="col-span-2">{SMPP_PORT}</dd>
            <dt className="text-slate-500">System ID</dt><dd className="col-span-2 break-all">{created.system_id}</dd>
            <dt className="text-slate-500">Password</dt><dd className="col-span-2 break-all">{created.password}</dd>
          </dl>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="rounded border px-3 py-1"
              onClick={async () => {
                try { await navigator.clipboard.writeText(text); setCopied(true); }
                catch { window.prompt('Copy the credentials:', text); }
              }}
            >
              {copied ? 'Copied' : 'Copy all'}
            </button>
            <button
              type="button"
              className="rounded bg-blue-600 px-3 py-1 text-white"
              onClick={() => { const id = created.id; reset(); onOpenReseller(id); }}
            >
              Open reseller
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  const field = 'mt-1 w-full rounded border px-2 py-1';
  return (
    <Modal open={open} title="New SMPP reseller" onClose={close}>
      <form onSubmit={submit} className="max-h-[70vh] space-y-3 overflow-y-auto text-sm">
        <label className="block">
          Business name *
          <input required maxLength={255} value={name} onChange={(e) => setName(e.target.value)} className={field} />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            Contact email
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} />
          </label>
          <label className="block">
            Contact phone
            <input maxLength={20} value={phone} onChange={(e) => setPhone(e.target.value)} className={field} />
          </label>
        </div>
        <label className="block">
          Tier
          <select value={tier} onChange={(e) => setTier(e.target.value as NewReseller['tier_level'])} className={field}>
            {TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            Billing
            <select value={billing} onChange={(e) => setBilling(e.target.value as NewReseller['billing_model'])} className={field}>
              <option value="prepaid">Prepaid (add credit after creating)</option>
              <option value="postpaid">Postpaid</option>
            </select>
          </label>
          {billing === 'postpaid' && (
            <label className="block">
              Credit limit ($)
              <input type="number" min="0" step="0.01" required value={creditLimit}
                onChange={(e) => setCreditLimit(e.target.value)} className={field} />
            </label>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <label className="block">
            Max binds
            <input type="number" min="1" max="100" required value={maxConnections}
              onChange={(e) => setMaxConnections(e.target.value)} className={field} />
          </label>
          <label className="block">
            Msgs / second
            <input type="number" min="1" max="10000" required value={ratePerSecond}
              onChange={(e) => setRatePerSecond(e.target.value)} className={field} />
          </label>
          <label className="block">
            Daily quota
            <input type="number" min="0" required value={dailyQuota}
              onChange={(e) => setDailyQuota(e.target.value)} className={field} />
          </label>
        </div>
        <label className="block">
          Allowed IPs (optional, comma-separated; others can't bind)
          <input placeholder="203.0.113.5, 198.51.100.0/24" value={ips}
            onChange={(e) => setIps(e.target.value)} className={field} />
        </label>
        <p className="text-xs text-slate-500">System ID and password are generated and shown once.</p>
        {error && <p className="text-red-700">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={close} className="rounded border px-3 py-1">Cancel</button>
          <button type="submit" disabled={submitting} className="rounded bg-blue-600 px-3 py-1 text-white disabled:opacity-50">
            {submitting ? 'Creating…' : 'Create reseller'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
